import React, { useState, useMemo, useEffect } from 'react'
import { ChallengeService } from '@duck/api-definitions'
import { ChallengeTableFilters } from './ChallengeTableFilters'
import { ChallengesTable } from './ChallengesTable'
import { ChallengeDialog } from './ChallengeDialog'
import { baseColumns, type Column } from './utils'
import { apis } from '../../modules/apiClients'
import { useTar } from '@/modules'
import { useAtomValue } from 'jotai'
import { accessTokenAtom } from '../../store/userAtoms'

const STORAGE_KEY = 'admin-challenge-filters'

interface FilterState {
  sortField: keyof ChallengeService.Challenge
  sortDirection: 'asc' | 'desc'
  filterText: string
  statusFilter: string[]
  feedbackStatusFilter: string[]
  visibleColumnKeys: string[]
}

// Utility functions for localStorage
const loadFiltersFromStorage = (): FilterState | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

const saveFiltersToStorage = (filters: FilterState): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
  } catch {
    // Ignore localStorage errors
  }
}

const reconstructColumns = (visibleKeys?: string[]): Column[] => {
  const keys = visibleKeys || baseColumns.filter(col => col.visible).map(col => col.key)
  return baseColumns.map(col => ({
    ...col,
    visible: keys.includes(col.key)
  }))
}

export const ChallengeManagement: React.FC = () => {
  const { t } = useTar()

  const accessToken = useAtomValue(accessTokenAtom)
  // Initialize state from localStorage or defaults
  const storedFilters = loadFiltersFromStorage()

  const [sortField, setSortField] = useState<keyof ChallengeService.Challenge>(
    storedFilters?.sortField ?? 'startTime'
  )
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    storedFilters?.sortDirection ?? 'desc'
  )
  const [filterText, setFilterText] = useState(storedFilters?.filterText ?? '')
  const [statusFilter, setStatusFilter] = useState<string[]>(
    Array.isArray(storedFilters?.statusFilter)
      ? (storedFilters?.statusFilter as string[])
      : (storedFilters?.statusFilter ? [storedFilters?.statusFilter as unknown as string] : [])
  )
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState<string[]>(
    Array.isArray(storedFilters?.feedbackStatusFilter)
      ? (storedFilters?.feedbackStatusFilter as string[])
      : (storedFilters?.feedbackStatusFilter ? [storedFilters?.feedbackStatusFilter as unknown as string] : [])
  )
  const [columnDropdownOpen, setColumnDropdownOpen] = useState(false)
  const [columns, setColumns] = useState<Column[]>(
    () => reconstructColumns(storedFilters?.visibleColumnKeys)
  )

  // API state
  const [challenges, setChallenges] = useState<ChallengeService.ChallengeInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Dialog state
  const [isChallengeDialogOpen, setIsChallengeDialogOpen] = useState(false)
  const [selectedChallenge, setSelectedChallenge] = useState<ChallengeService.ChallengeInfo | null>(null)

  // Fetch challenges from API
  const fetchChallenges = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apis.challengeService.getAllChallenges()
      setChallenges(response.data)
    } catch {
      setError('Failed to load challenges. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchChallenges()
  }, [])

  // Save to localStorage whenever filter state changes
  useEffect(() => {
    const visibleColumnKeys = columns.filter(col => col.visible).map(col => col.key)
    const filterState: FilterState = {
      sortField,
      sortDirection,
      filterText,
      statusFilter,
      feedbackStatusFilter,
      visibleColumnKeys,
    }
    saveFiltersToStorage(filterState)
  }, [sortField, sortDirection, filterText, statusFilter, feedbackStatusFilter, columns])

  const filteredAndSortedChallenges = useMemo(() => {
    let filtered = challenges

    // Text filter
    if (filterText) {
      const searchTerm = filterText.toLowerCase()
      filtered = filtered.filter(challengeInfo =>
        challengeInfo.challenge.userId.toLowerCase().includes(searchTerm) ||
        challengeInfo.challenge.taskId.toLowerCase().includes(searchTerm) ||
        challengeInfo.challenge.id.toLowerCase().includes(searchTerm)
      )
    }

    // Status filter (multi)
    if (statusFilter.length > 0) {
      const statusSet = new Set(statusFilter)
      filtered = filtered.filter(challengeInfo => statusSet.has(challengeInfo.challenge.status as string))
    }

    // Feedback status filter (multi)
    if (feedbackStatusFilter.length > 0) {
      const fbSet = new Set(feedbackStatusFilter)
      filtered = filtered.filter(challengeInfo => fbSet.has(challengeInfo.challenge.feedbackStatus as string))
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      const aValue = a.challenge[sortField]
      const bValue = b.challenge[sortField]

      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      let comparison = 0
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue)
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue
      } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        comparison = aValue === bValue ? 0 : aValue ? 1 : -1
      } else {
        comparison = String(aValue).localeCompare(String(bValue))
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return sorted
  }, [challenges, filterText, statusFilter, feedbackStatusFilter, sortField, sortDirection])

  const handleSort = (field: keyof ChallengeService.Challenge) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const toggleColumnVisibility = (columnKey: string) => {
    setColumns(prev => prev.map(col =>
      col.key === columnKey
        ? { ...col, visible: !col.visible }
        : col
    ))
  }

  const handleChallengeClick = (challengeInfo: ChallengeService.ChallengeInfo) => {
    setSelectedChallenge(challengeInfo)
    setIsChallengeDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsChallengeDialogOpen(false)
    setSelectedChallenge(null)
  }

  const visibleColumns = columns.filter(col => col.visible)

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold leading-7 text-foreground sm:truncate sm:text-3xl sm:tracking-tight">
            {t('challenges.challengeManagement')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('challenges.challengeManagementDescription')}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => fetchChallenges()}
            className="px-4 py-2 bg-secondary text-foreground rounded-md text-sm font-medium hover:bg-secondary/80 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {/* Filters and Controls */}
      <ChallengeTableFilters
        filterText={filterText}
        setFilterText={setFilterText}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        feedbackStatusFilter={feedbackStatusFilter}
        setFeedbackStatusFilter={setFeedbackStatusFilter}
        columns={columns}
        columnDropdownOpen={columnDropdownOpen}
        setColumnDropdownOpen={setColumnDropdownOpen}
        toggleColumnVisibility={toggleColumnVisibility}
      />

      {/* Error State */}
      {error && (
        <div className="rounded-md bg-error/10 border border-error/20 p-4">
          <div className="text-error text-sm">{error}</div>
        </div>
      )}

      {/* Challenges Table */}
      {!error && (
        <ChallengesTable
          challenges={filteredAndSortedChallenges}
          totalChallenges={challenges.length}
          visibleColumns={visibleColumns}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          onChallengeClick={handleChallengeClick}
          loadingMode={loading}
        />
      )}

      {/* Challenge Dialog */}
      {selectedChallenge && <ChallengeDialog
        isOpen={isChallengeDialogOpen}
        onClose={handleCloseDialog}
        challenge={selectedChallenge}
        onDelete={fetchChallenges}
      />}
    </div>
  )
}
